import { resolveCaseDetailValue } from 'utils/tinyMCE/updateDynamicText/resolveCaseDetailValue'
import { resolvePath } from './resolvePath'
import { findDeepestTextElement } from './findDeepestTextElement'
import {
  canRemoveSeparator,
  inkOf,
  isBlankInkText,
  isGluedAroundHidden,
  namePartFieldKeys,
} from './namePartSeparators'
import get from 'lodash/get'
import { escapeHtml } from '@messenger/utils'

// ─── Helpers ──────────────────────────────────────────────────

const isNodeHidden = (node) => {
  const display = node.css('display')
  if (display === 'none') return true
  const style = node.attr('style') || ''
  return style.includes('display:none') || style.includes('display: none')
}

/**
 * A "space span" is a styled <span> containing only whitespace, used as a
 * separator between name parts. TinyMCE and the Name Order plugin create
 * these instead of text nodes because TinyMCE normalizes away text nodes
 * between mceNonEditable spans.
 */
const isSpaceSpan = (node) => {
  if (node[0].type !== 'tag') return false
  if (node.attr('data-case-detail')) return false
  const t = node.text()
  return t.trim() === '' && t.length > 0
}

/**
 * Walk siblings in `direction` (1 = forward, -1 = backward) looking for a
 * space separator to restore or a visible sibling that proves we need one.
 *
 * Returns: { hiddenSpaceSpan, needsSpace }
 */
const findAdjacentSpace = (parent, startIdx, direction) => {
  const contents = parent.contents()
  let hiddenSpaceSpan = null
  let needsSpace = false

  const end = direction === 1 ? contents.length : -1
  for (let k = startIdx; k !== end; k += direction) {
    const node = contents.eq(k)
    if (node[0].type === 'text') {
      const ink = inkOf(node.text())
      if (ink === '') {
        // Zero-width guard chars (TinyMCE FEFF/ZWSP) render nothing —
        // neither separator nor content; keep scanning
        continue
      }
      if (ink.trim() === '') {
        // Whitespace text node = separator already exists
        break
      }
      // Mixed text node: a separator can live at the node's gap-facing edge —
      // template text ending in a non-collapsible space ("The family
      // of\u00A0") is a real corpus shape, and /\s/ matches \u00A0. Mirrors
      // scanSide's gapEdge check; without it the show path stacks a plain
      // space onto the nbsp (the 15.44px double gap in the measured
      // whitespace table) that no later heal pass ever repairs.
      const gapEdge = direction === -1 ? ink[ink.length - 1] : ink[0]
      if (/\s/.test(gapEdge)) {
        break
      }
      // Visible text directly adjacent = a separator is needed
      needsSpace = true
      break
    }
    if (node[0].type === 'tag') {
      const hidden = isNodeHidden(node)
      if (!hidden) {
        // Visible space span = separator exists
        if (isSpaceSpan(node)) break
        needsSpace = true
        break
      }
      // Hidden space span = can be restored
      if (hidden && isSpaceSpan(node)) {
        hiddenSpaceSpan = node
        continue
      }
    }
  }
  return { hiddenSpaceSpan, needsSpace }
}

// ─── Main ─────────────────────────────────────────────────────

export const dynamicTextUtilities = (dynamicTextProps) => ({
  getCaseDetail: (element) => {
    const caseDetailAttribute = element.attr('data-case-detail')
    const caseDetailTableAttribute = element.attr('data-case-detail-table')

    const attribute = caseDetailAttribute || caseDetailTableAttribute

    const path = attribute?.split(' ')?.[0]
    const initialText = element.text()

    const { caseDetails, funeralHomeDetails } = dynamicTextProps.editorProps
    const resolvedPath = resolvePath(path, caseDetails)
    const caseDetail = get(caseDetails, resolvedPath)
    const funeralHomeDetail = get({ funeralHomeDetails }, resolvedPath)
    const value = caseDetail || funeralHomeDetail || '\u200A'

    return resolveCaseDetailValue({
      path,
      initialText,
      value,
      caseDetails,
    })
  },

  getHeader: (element) => {
    const headerAttribute = element.attr('data-template')

    if (!headerAttribute) {
      return null
    }

    const path = headerAttribute?.split(' ')?.[0]
    const initialText = element.text()

    const { caseDetails } = dynamicTextProps.editorProps
    const { case_preferences } = caseDetails || {}

    if (!case_preferences) {
      return {
        path,
        value: '\u200A',
        initialText,
      }
    }

    const headerValue = get(case_preferences, path)
    const value = headerValue || '\u200A'

    return {
      path,
      value,
      initialText,
    }
  },

  updateDeepestSpanText: (element, text) => {
    let deepestSpan = element

    while (deepestSpan.find('span').length > 0) {
      deepestSpan = deepestSpan.find('span').first()
    }

    const textContent = String(text || '')
    const dataAttribute = element.attr('data-case-detail')

    const isNameField = namePartFieldKeys.includes(dataAttribute)
    const hidingNameFields = textContent === '\u200A' && isNameField
    if (hidingNameFields) {
      const alreadyHidden = element.css('display') === 'none'
      if (alreadyHidden) {
        // Clean up any pre-existing orphaned spaces from old HTML patterns.
        // A space is only an orphan when it is not the sole separator between
        // two visible name parts — removing that one would glue them together.
        const parent = element.parent()
        const idx = parent.contents().index(element)
        const prevN = parent.contents().eq(idx - 1)
        if (
          prevN.length &&
          prevN[0].nodeType === 3 &&
          isBlankInkText(prevN.text()) &&
          canRemoveSeparator(prevN[0])
        ) {
          prevN.remove()
        }
        const updIdx = parent.contents().index(element)
        const nextN = parent.contents().eq(updIdx + 1)
        if (
          nextN.length &&
          nextN[0].nodeType === 3 &&
          isBlankInkText(nextN.text())
        ) {
          if (canRemoveSeparator(nextN[0])) {
            nextN.remove()
          }
        } else if (
          nextN.length &&
          isSpaceSpan(nextN) &&
          !isNodeHidden(nextN) &&
          canRemoveSeparator(nextN[0])
        ) {
          nextN.css('display', 'none')
        }

        const prevSibling = element.prev()
        if (
          prevSibling.length &&
          prevSibling.is('span') &&
          isSpaceSpan(prevSibling) &&
          !isNodeHidden(prevSibling) &&
          canRemoveSeparator(prevSibling[0])
        ) {
          prevSibling.css('display', 'none')
        }

        // Repair content saved glued by versions that removed both sides.
        // Bare space by design — style-neutral and wraps like typed text; if
        // TinyMCE ever normalizes it away, the next update pass re-detects
        // the glue and re-heals (see isGluedAroundHidden).
        if (isGluedAroundHidden(element[0])) element.before(' ')
        return
      }

      const parent = element.parent()

      // Hide first so separator analysis sees the post-hide reality: spaces
      // next to this span are then removable only when they are edge orphans
      // or another separator still bridges the gap between visible parts.
      element.css('display', 'none')

      const elementIndex = parent.contents().index(element)
      const prevNode = parent.contents().eq(elementIndex - 1)
      const spanHadSpaceBefore =
        prevNode.length &&
        prevNode[0].nodeType === 3 &&
        isBlankInkText(prevNode.text())
      if (spanHadSpaceBefore && canRemoveSeparator(prevNode[0])) {
        prevNode.remove()
      }

      // Hide trailing space (text node or span element)
      const updatedIndex = parent.contents().index(element)
      const nextNode = parent.contents().eq(updatedIndex + 1)
      if (nextNode.length) {
        const isTrailingTextSpace =
          nextNode[0].nodeType === 3 && isBlankInkText(nextNode.text())
        if (isTrailingTextSpace && canRemoveSeparator(nextNode[0])) {
          nextNode.remove()
        } else if (
          isSpaceSpan(nextNode) &&
          !isNodeHidden(nextNode) &&
          canRemoveSeparator(nextNode[0])
        ) {
          nextNode.css('display', 'none')
        }
      }

      const prevSibling = element.prev()
      if (
        prevSibling.length &&
        prevSibling.is('span') &&
        isSpaceSpan(prevSibling) &&
        !isNodeHidden(prevSibling) &&
        canRemoveSeparator(prevSibling[0])
      ) {
        prevSibling.css('display', 'none')
      }

      // Sweep ALL hidden name spans across all parents for orphaned spaces.
      // Handles pre-existing hidden spans from old .join(' ') HTML patterns.
      let root = parent
      while (root.parent().length && root.parent()[0].type === 'tag') {
        root = root.parent()
      }

      const sweepParents = new Set()
      root.find('[data-case-detail]').each(function () {
        const span = root.find(this)
        if (
          isNodeHidden(span) &&
          namePartFieldKeys.includes(span.attr('data-case-detail'))
        ) {
          sweepParents.add(span.parent()[0])
        }
      })

      for (const parentNode of sweepParents) {
        const p = root.find(parentNode)
        let swept = true
        while (swept) {
          swept = false
          const contents = p.contents()
          for (let i = 0; i < contents.length; i++) {
            const node = contents.eq(i)
            if (node[0].type !== 'tag') continue
            if (!namePartFieldKeys.includes(node.attr('data-case-detail')))
              continue
            if (!isNodeHidden(node)) continue

            if (i > 0) {
              const prev = contents.eq(i - 1)
              if (
                prev[0].type === 'text' &&
                isBlankInkText(prev.text()) &&
                canRemoveSeparator(prev[0])
              ) {
                prev.remove()
                swept = true
                break
              }
              if (
                isSpaceSpan(prev) &&
                !isNodeHidden(prev) &&
                canRemoveSeparator(prev[0])
              ) {
                prev.css('display', 'none')
                swept = true
                break
              }
            }

            const newIdx = p.contents().index(node)
            const next = p.contents().eq(newIdx + 1)
            if (next.length) {
              if (
                next[0].type === 'text' &&
                isBlankInkText(next.text()) &&
                canRemoveSeparator(next[0])
              ) {
                next.remove()
                swept = true
                break
              }
              if (
                isSpaceSpan(next) &&
                !isNodeHidden(next) &&
                canRemoveSeparator(next[0])
              ) {
                next.css('display', 'none')
                swept = true
                break
              }
            }
          }
        }
      }

      // Repair content saved glued by versions that removed both sides —
      // bare space by design (see isGluedAroundHidden)
      if (isGluedAroundHidden(element[0])) element.before(' ')

      return
    }

    const isHidden = element.css('display') === 'none'
    const hasValue = textContent !== '\u200A'
    const isUserHidden = element.attr('data-user-hidden') === 'true'
    const showingPreviouslyHiddenSpan = isHidden && hasValue && !isUserHidden

    if (showingPreviouslyHiddenSpan) {
      element.css('display', '')

      const parent = element.parent()

      // Restore separators from what the DOM actually needs — a separator may
      // have been kept while this span was hidden (it was load-bearing
      // between two visible parts), in which case inserting another would
      // double it. (Old saved content may still carry inert data-had-space
      // attributes from retired bookkeeping; nothing reads them.) Prefer
      // restoring an existing hidden space span over inserting a raw text
      // node -- TinyMCE normalizes text nodes between mceNonEditable spans,
      // so an unhidden span is more durable.
      const idx = parent.contents().index(element)
      const before = findAdjacentSpace(parent, idx - 1, -1)
      if (before.hiddenSpaceSpan) {
        before.hiddenSpaceSpan.css('display', '')
      } else if (before.needsSpace) {
        // Bare-space fallback: if TinyMCE normalizes it away, the next
        // update pass sees needsSpace again and re-inserts — never data loss
        element.before(' ')
      }

      const updIdx = parent.contents().index(element)
      const after = findAdjacentSpace(parent, updIdx + 1, 1)
      if (after.hiddenSpaceSpan) {
        after.hiddenSpaceSpan.css('display', '')
      } else if (after.needsSpace) {
        // Same bare-space fallback as above
        element.after(' ')
      }
    }

    if (isHidden && isUserHidden && isGluedAroundHidden(element[0])) {
      // Name-Order-hidden spans (value kept, user chose to hide) sit inside
      // glued saved content too; sanitizeDynamicText heals them on the
      // preview/PDF path — the editor must match, or damaged orders render
      // glued exactly where staff verify them. Bare space by design (see
      // isGluedAroundHidden).
      element.before(' ')
    }

    const deepestTextElement = findDeepestTextElement(deepestSpan)

    if (!deepestTextElement || deepestTextElement.length === 0) {
      /**
       * Fallback when findDeepestTextElement returns null.
       *
       * This happens when nested spans contain only whitespace, which findDeepestTextElement
       * filters out using .trim(). Only insert text if the span truly contains whitespace
       * to avoid corrupting any unexpected nested structure.
       */
      const existingText = deepestSpan?.text?.()
      const hasOnlyWhitespace = !existingText || !existingText?.trim?.()

      if (hasOnlyWhitespace) {
        deepestSpan?.text?.(textContent)
      }
      return
    }

    deepestTextElement
      .contents()
      .filter(function () {
        return this.type === 'text'
      })
      // cheerio parses a replacement string as HTML: escaped, the value is
      // inserted as the text it is.
      .replaceWith(escapeHtml(textContent))
  },
})

export default dynamicTextUtilities
